import { Moment } from 'moment';
import { Column, Icon } from '@odin-labs/components';
import { GetJobsiteFormSubmissionsQuery } from 'apollo/generated/client-operations';
import { CustomForm, FormsTabApi } from 'containers/forms/types';
import { DateRangeFilterValues, PageSearchParams } from 'utils';
import { ReplaceSelectOptionElement } from 'types';

type JobsiteFormSubmissions =
  GetJobsiteFormSubmissionsQuery['getCurrentSession']['user']['jobsiteFormSubmissions'];
export type FormSubmission = JobsiteFormSubmissions['edges'][number]['node'];
export type FormSubmissionFiltersOptions =
  JobsiteFormSubmissions['filtersOptions'];

export type TypedFormSubmission<TExtraData extends Record<string, unknown>> =
  Omit<FormSubmission, 'extraData'> & {
    extraData?: ReplaceSelectOptionElement<TExtraData>;
  };

export type FormSubmissionFilters = Pick<
  PageSearchParams,
  | 'developerIds'
  | 'jobsiteIds'
  | 'contractorIds'
  | 'createdByIds'
  | 'formIds'
  | 'hasAttachments'
  | 'workerIds'
  | 'responses'
  | 'trainings'
> & {
  dateRange: {
    startDate: Moment;
    endDate: Moment;
  };
};

export type DefaultExtraData = Record<string, unknown>;

export type FormSubmissionColumn<
  TExtraData extends DefaultExtraData = DefaultExtraData,
> = Column<TypedFormSubmission<TExtraData>> & {
  headerIcon?: Icon;
  mobile?: {
    headerIcon?: Icon;
    cellClassName?: string;
    cellContainerClassName?: string;
  };
};

export type FormSubmissionsWidgetByForm = { form: CustomForm };
export type FormSubmissionsWidgetByWorker = {
  workerId: string;
  extraColumns?: FormSubmissionColumn[];
  filters?: Array<keyof FormSubmissionFilters>;
  navigateInNewTab?: boolean;
};
export type FormSubmissionsWidgetCommonProps = {
  onTabApiChange: (api: FormsTabApi) => void;
  // Optional shared date range: when provided, the widget seeds its date filter
  // defaults from `dateRange` and writes user changes back via `setDateRange`.
  dateRange?: DateRangeFilterValues;
  setDateRange?: (changes: DateRangeFilterValues) => void;
};
export type FormSubmissionsWidgetProps = FormSubmissionsWidgetCommonProps &
  (FormSubmissionsWidgetByForm | FormSubmissionsWidgetByWorker);

export enum FormColumnName {
  JobsiteContractor = 'jobsiteContractor',
  Form = 'form',
  Details = 'details',
  Manpower = 'manpower',
  Date = 'date',
  StartTime = 'startTime',
  EndTime = 'endTime',
  CreatedBy = 'createdBy',
  Actions = 'actions',
  Building = 'building',
  Incident = 'incident',
  ReportedBy = 'reportedBy',
  ObservationType = 'observationType',
  WorkersCount = 'workersCount',
  InspectedBy = 'inspectedBy',
  InspectionType = 'inspectionType',
  ExtinguisherNo = 'extinguisherNo',
  Level = 'level',
  TimeIn = 'timeIn',
  TempBadge = 'tempBadge',
  TempBadgeExpiration = 'tempBadgeExpiration',
  Visitor = 'visitor',
  AssignedTo = 'assignedTo',
  Response = 'response',
  RespondedAt = 'respondedAt',
}
